import {
  CATEGORY_ID_CORAL,
  CATEGORY_ID_EQUIPMENT,
  CATEGORY_ID_FISH,
  LISTING_TYPE_DRY_GOODS,
  LISTING_TYPE_LIVE_GOODS,
  LISTING_TYPE_REEF_ART_APPAREL,
} from './r2rListingTypeCategories';

const ICON_ROOT = '/static/icons/mobile-search-quick-tags';
/** Bump when replacing Quick tag PNGs so phones skip the 31-day static cache. */
const ICON_CACHE_BUST = '20260823';

/**
 * Ordered Quick tags for the mobile search overlay.
 * Edit this list to change the group later.
 *
 * Level-2 ids must match hosted listing-categories.json. Search drops unknown
 * `pub_categoryLevel2` values, which leaves only listing type + top-level category.
 *
 * @typedef {Object} MobileSearchQuickTag
 * @property {string} id
 * @property {string} labelId
 * @property {string} icon filename stem under light/ and dark/
 * @property {boolean} [hasLightIcon] default true; false falls back to dark PNG
 * @property {Object<string, string>} searchParams
 */

/** @type {MobileSearchQuickTag[]} */
export const MOBILE_SEARCH_QUICK_TAGS = [
  {
    id: 'sps',
    labelId: 'MobileSearchQuickTags.tag.sps',
    icon: 'sps',
    searchParams: {
      pub_listingType: LISTING_TYPE_LIVE_GOODS,
      pub_categoryLevel1: CATEGORY_ID_CORAL,
      pub_categoryLevel2: 'sps_livegoods',
    },
  },
  {
    id: 'lps',
    labelId: 'MobileSearchQuickTags.tag.lps',
    icon: 'lps',
    searchParams: {
      pub_listingType: LISTING_TYPE_LIVE_GOODS,
      pub_categoryLevel1: CATEGORY_ID_CORAL,
      pub_categoryLevel2: 'lps_livegoods',
    },
  },
  {
    id: 'softies',
    labelId: 'MobileSearchQuickTags.tag.softies',
    icon: 'softies',
    searchParams: {
      pub_listingType: LISTING_TYPE_LIVE_GOODS,
      pub_categoryLevel1: CATEGORY_ID_CORAL,
      pub_categoryLevel2: 'Soft_coral_livegoods',
    },
  },
  {
    id: 'zoas',
    labelId: 'MobileSearchQuickTags.tag.zoas',
    icon: 'zoas',
    searchParams: {
      pub_listingType: LISTING_TYPE_LIVE_GOODS,
      pub_categoryLevel1: CATEGORY_ID_CORAL,
      pub_categoryLevel2: 'Soft_Zoanthids',
    },
  },
  {
    id: 'fish',
    labelId: 'MobileSearchQuickTags.tag.fish',
    icon: 'fish',
    searchParams: {
      pub_listingType: LISTING_TYPE_LIVE_GOODS,
      pub_categoryLevel1: CATEGORY_ID_FISH,
    },
  },
  {
    id: 'inverts',
    labelId: 'MobileSearchQuickTags.tag.inverts',
    icon: 'inverts',
    searchParams: {
      pub_listingType: LISTING_TYPE_LIVE_GOODS,
      pub_categoryLevel1: 'inverts_livegoods',
    },
  },
  {
    id: 'anemones',
    labelId: 'MobileSearchQuickTags.tag.anemones',
    icon: 'anemones',
    searchParams: {
      pub_listingType: LISTING_TYPE_LIVE_GOODS,
      pub_categoryLevel1: 'inverts_anemones',
    },
  },
  {
    id: 'skimmers',
    labelId: 'MobileSearchQuickTags.tag.skimmers',
    icon: 'skimmers',
    searchParams: {
      pub_listingType: LISTING_TYPE_DRY_GOODS,
      pub_categoryLevel1: CATEGORY_ID_EQUIPMENT,
      pub_categoryLevel2: 'equipment_skimmers',
    },
  },
  {
    id: 'pumps',
    labelId: 'MobileSearchQuickTags.tag.pumps',
    icon: 'pumps',
    searchParams: {
      pub_listingType: LISTING_TYPE_DRY_GOODS,
      pub_categoryLevel1: CATEGORY_ID_EQUIPMENT,
      pub_categoryLevel2: 'equipment_pumps_flow',
    },
  },
  {
    id: 'lights',
    labelId: 'MobileSearchQuickTags.tag.lights',
    icon: 'lights',
    searchParams: {
      pub_listingType: LISTING_TYPE_DRY_GOODS,
      pub_categoryLevel1: CATEGORY_ID_EQUIPMENT,
      pub_categoryLevel2: 'equipment_lighting',
    },
  },
  {
    id: 'tanks',
    labelId: 'MobileSearchQuickTags.tag.tanks',
    icon: 'tanks',
    searchParams: {
      pub_listingType: LISTING_TYPE_DRY_GOODS,
      pub_categoryLevel1: CATEGORY_ID_EQUIPMENT,
      pub_categoryLevel2: 'equipment_tanks',
    },
  },
  {
    id: 'test-kits',
    labelId: 'MobileSearchQuickTags.tag.testKits',
    icon: 'test-kits',
    searchParams: {
      pub_listingType: LISTING_TYPE_DRY_GOODS,
      pub_categoryLevel1: CATEGORY_ID_EQUIPMENT,
      pub_categoryLevel2: 'equipment_testing_monitoring',
    },
  },
  {
    id: 'salt-plus-additives',
    labelId: 'MobileSearchQuickTags.tag.saltPlusAdditives',
    icon: 'salt-plus-additives',
    searchParams: {
      pub_listingType: LISTING_TYPE_DRY_GOODS,
      pub_categoryLevel1: CATEGORY_ID_EQUIPMENT,
      pub_categoryLevel2: 'equipment_salt,equipment_supplements',
    },
  },
  {
    id: 'wysiwyg',
    labelId: 'MobileSearchQuickTags.tag.wysiwyg',
    icon: 'wysiwyg',
    searchParams: {
      pub_listingType: LISTING_TYPE_LIVE_GOODS,
      pub_categoryLevel1: CATEGORY_ID_CORAL,
      pub_wysiwyg_coral: 'wysiwyg_yes',
    },
  },
  {
    id: 'macroalgae',
    labelId: 'MobileSearchQuickTags.tag.macroalgae',
    icon: 'macroalgae',
    searchParams: {
      pub_listingType: LISTING_TYPE_LIVE_GOODS,
      pub_categoryLevel1: 'Plants_macro_livegoods',
      pub_categoryLevel2: 'marine_plants_macroalgae',
    },
  },
  {
    id: 'apparel',
    labelId: 'MobileSearchQuickTags.tag.apparel',
    icon: 'apparel',
    searchParams: {
      pub_listingType: LISTING_TYPE_REEF_ART_APPAREL,
      pub_categoryLevel1: 'apparel_drygoods',
    },
  },
];

/**
 * @param {MobileSearchQuickTag} tag
 * @returns {string} query string including leading `?`
 */
export const buildQuickTagSearch = tag => {
  const params = new URLSearchParams();
  Object.entries(tag.searchParams).forEach(([key, value]) => {
    if (value != null && value !== '') {
      params.set(key, String(value));
    }
  });
  return `?${params.toString()}`;
};

/**
 * @param {MobileSearchQuickTag} tag
 * @param {'light'|'dark'|string} colorScheme
 * @returns {string}
 */
export const quickTagIconSrc = (tag, colorScheme) => {
  const darkSrc = `${ICON_ROOT}/dark/${tag.icon}.png`;
  const src =
    colorScheme === 'dark' || tag.hasLightIcon === false
      ? darkSrc
      : `${ICON_ROOT}/light/${tag.icon}.png`;
  return `${src}?v=${ICON_CACHE_BUST}`;
};
